<template>
  <v-text-field
    v-model="datetime"
    v-maska="options"
    :label="label"
    :placeholder="placeholder"
    :density="dense"
    variant="outlined"
    :rules="rules"
    :loading="loading"
    color="primary"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import type DensityProp from '@/types/vuetify/Density'
import type { ValidationRule } from '@/types/vuetify/ValidationRule'
import type { MaskInputOptions } from 'maska'

interface MaskaDetail {
  masked: string
  unmasked: string
  completed: boolean
}

const props = withDefaults(defineProps<{
  label?: string
  dense?: DensityProp
  rules?: ValidationRule[]
  unmasked?: boolean
  loading?: boolean
  externalFormat?: string
  seconds?: boolean
  formatter?: (detail: MaskaDetail) => string
}>(), {
  label: 'Informe a data e hora',
  dense: 'compact',
  rules: () => [],
  unmasked: false,
  loading: false,
  externalFormat: 'yyyy-MM-dd HH:mm:ss',
  seconds: true,
  formatter: undefined,
})

const model = defineModel<string | null>({ default: null })

const displayFormat = computed(() => (props.seconds ? 'dd/MM/yyyy HH:mm:ss' : 'dd/MM/yyyy HH:mm'))
const placeholder = computed(() => (props.seconds ? '00/00/0000 00:00:00' : '00/00/0000 00:00'))

// Initialize datetime as a display string (dd/MM/yyyy HH:mm[:ss]) if modelValue is valid
function toDisplay(value: string | null): string {
  if (!value) return ''
  const dt = DateTime.fromFormat(value, props.externalFormat)
  return dt.isValid ? dt.toFormat(displayFormat.value) : ''
}

const datetime = ref(toDisplay(model.value))

const options: MaskInputOptions = {
  mask: props.seconds ? '##/##/#### ##:##:##' : '##/##/#### ##:##',
}

// when parent updates modelValue, reflect it in the internal display
watch(model, (_new) => {
  if (!_new) {
    const parsedInternal = DateTime.fromFormat(datetime.value, displayFormat.value)
    if (parsedInternal.isValid || datetime.value === '') {
      datetime.value = ''
    }

    return
  }

  const formatted = DateTime.fromFormat(_new, props.externalFormat)

  if (formatted.isValid) {
    const displayVal = formatted.toFormat(displayFormat.value)
    if (datetime.value !== displayVal) datetime.value = displayVal
  }
})

// keep internal changes in sync: when datetime becomes a valid value (user input via mask), emit to parent
watch(datetime, (newVal) => {
  if (!newVal) {
    if (model.value !== null) model.value = null
    return
  }

  if (newVal.length === displayFormat.value.length) {
    const parsed = DateTime.fromFormat(newVal, displayFormat.value)
    if (parsed.isValid) {
      const formatted = parsed.toFormat(props.externalFormat)
      if (model.value !== formatted) model.value = formatted
    } else if (model.value !== null) {
      model.value = null
    }
  } else if (model.value !== null) {
    model.value = null
  }
})
</script>
